import React, { useEffect, useState } from 'react';
import logo from './logo.jpg'; // Import the logo
import axios from 'axios';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, ResponsiveContainer, LabelList } from 'recharts';
import { Download, Plus, FileSpreadsheet, FileText, AlertTriangle, MessageCircle, CheckCircle, Edit, TrendingUp, Search, Trash2, Users, Shield, X, Save, Copy, Truck, CheckCheck, PackageCheck, Zap, RefreshCw, Layers } from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { CONSTRUCTION_ITEMS } from './items';


const COLORS = ['#ef4444', '#eab308', '#f97316', '#10b981'];
const PROJECT_LIST = ['Business Hub', 'One Destination', 'Bodla Homes 5.2 Marla', 'Bodla Homes 8 Marla', 'Oasis Farmhouse', 'GVR'];

export default function Dashboard({ user, onLogout, onBackToPortal }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [filteredData, setFilteredData] = useState([]);

  // Multi-Item State
  const [demandItems, setDemandItems] = useState([{ item: '', unit: '', demanded_qty: '', description: '' }]);
  const [demandHeader, setDemandHeader] = useState({
    id: '',
    job_number: '',
    project: '',
    priority: 'Medium',
    raised_on: new Date().toISOString().split('T')[0],
    required_date: '',
    construction_remarks: ''
  });

  // Slip-Wide Supply Batch Update State
  const [isSlipModalOpen, setIsSlipModalOpen] = useState(false);
  const [selectedSlip, setSelectedSlip] = useState(null);
  const [slipBatchDate, setSlipBatchDate] = useState(new Date().toISOString().split('T')[0]);
  const [slipBatchRemarks, setSlipBatchRemarks] = useState('');
  const [showUserModal, setShowUserModal] = useState(false);
  const [activeTab, setActiveTab] = useState('home'); // 'home', 'activity', 'register', 'settings'

  // Real WhatsApp Integration State
  const [waStatus, setWaStatus] = useState({ status: 'Initializing...', qr: null });
  const [showQrModal, setShowQrModal] = useState(false);
  const [waConfig, setWaConfig] = useState({ groupName: '', inviteLink: '' });

  // Stats for Charts
  const [stats, setStats] = useState({
    demanded: 0, supplied: 0, partially_supplied: 0, pending: 0, delayed: 0
  });

  // Trend Data
  const [trendData, setTrendData] = useState([]);

  // Duration Selection State (Calendar Form)
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Filtered Items for Dropdown
  const [filteredItems, setFilteredItems] = useState(CONSTRUCTION_ITEMS);
  const [itemSearch, setItemSearch] = useState('');

  const fetchWaConfig = async () => {
    try {
      const res = await axios.get('/api/whatsapp/config');
      setWaConfig(res.data);
    } catch (e) {
      console.error("Error fetching WA config", e);
    }
  };

  const fetchData = async () => {
    try {
      const res = await axios.get('/api/data');
      setData(res.data);
      // Stats calculated via useEffect now
    } catch (err) {
      console.error("Error fetching data", err);
    }
  };

  const fetchWhatsAppStatus = async () => {
    try {
      const res = await axios.get('/api/whatsapp/status');
      setWaStatus(res.data);
    } catch (err) {
      // console.error("Error fetching WA status", err);
      setWaStatus({ status: 'Backend Error', qr: null });
    }
  };

  const calculateStats = (items) => {
    let d = 0, s = 0, ps = 0, p = 0, del = 0;
    const todayStr = new Date().toISOString().split('T')[0];
    items.forEach(i => {
      d += 1;
      const isComplete = i.supply_status === 'Complete' || i.status === 'Complete' || i.status === 'Supplied';
      const isPartial = i.supply_status === 'Partially Supplied' || i.status === 'Partially Supplied';
      const isOverdue = !isComplete && (i.timeliness === 'Delayed' || (i.required_date && i.required_date < todayStr));

      if (isComplete) {
        s += 1;
      } else if (isOverdue) {
        del += 1;
      } else if (isPartial) {
        ps += 1;
      } else {
        p += 1;
      }
    });
    setStats({ demanded: d, supplied: s, partially_supplied: ps, pending: p, delayed: del });
  };

  const calculateTrends = (items) => {
    // Group by Date (Raised On vs Supplied On)
    const dates = {};

    // Initialize dates from items
    items.forEach(i => {
      if (i.raised_on) dates[i.raised_on] = { date: i.raised_on, demand: 0, supply: 0 };
      if (i.supplied_date) dates[i.supplied_date] = { date: i.supplied_date, demand: 0, supply: 0 };
    });

    // Count
    items.forEach(i => {
      if (dates[i.raised_on]) dates[i.raised_on].demand += 1;
      if (i.supplied_date && dates[i.supplied_date]) dates[i.supplied_date].supply += 1;
    });

    // Convert to Array & Sort
    const sorted = Object.values(dates).sort((a, b) => new Date(a.date) - new Date(b.date));
    // Take last 10 data points for better visuals
    setTrendData(sorted.slice(-10));
  };

  const syncOfflineData = async () => {
    if (!navigator.onLine) return;
    const offlineRequests = JSON.parse(localStorage.getItem('offlineRequests') || '[]');
    if (offlineRequests.length === 0) return;

    let remaining = [];
    let syncedCount = 0;

    for (const req of offlineRequests) {
      try {
        if (req.type === 'create') {
          await axios.post('/api/demand', req.payload);
        } else if (req.type === 'update') {
          await axios.put(`/api/supply/${req.id}`, req.payload);
        }
        syncedCount++;
      } catch (err) {
        // Keep in queue if server error
        remaining.push(req);
      }
    }

    localStorage.setItem('offlineRequests', JSON.stringify(remaining));
    if (syncedCount > 0) {
      fetchData();
      alert(`✅ Welcome back online! Successfully synced ${syncedCount} queued request(s).`);
    }
  };

  useEffect(() => {
    fetchData();
    fetchWhatsAppStatus();
    fetchWaConfig();
    const interval = setInterval(() => {
      fetchData();
      fetchWhatsAppStatus();
      syncOfflineData();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let res = data;
    if (filterText) {
      const lower = filterText.trim().toLowerCase();
      res = res.filter(d => {
        const valMatches = (val) => val && String(val).toLowerCase().includes(lower);
        return valMatches(d.id) ||
               valMatches(d.job_number) ||
               valMatches(d.project) ||
               valMatches(d.item) ||
               valMatches(d.description) ||
               valMatches(d.unit) ||
               valMatches(d.priority) ||
               valMatches(d.supply_status) ||
               valMatches(d.timeliness) ||
               valMatches(d.initiator) ||
               valMatches(d.construction_remarks) ||
               valMatches(d.procurement_remarks) ||
               valMatches(d.required_date) ||
               valMatches(d.supplied_date);
      });
    }

    if (fromDate) {
      res = res.filter(d => {
        const itemDate = d.required_date || d.supplied_date || d.raised_on || d.created_at;
        if (!itemDate) return true;
        const dStr = String(itemDate).substring(0, 10);
        return dStr >= fromDate;
      });
    }

    if (toDate) {
      res = res.filter(d => {
        const itemDate = d.required_date || d.supplied_date || d.raised_on || d.created_at;
        if (!itemDate) return true;
        const dStr = String(itemDate).substring(0, 10);
        return dStr <= toDate;
      });
    }

    setFilteredData(res);
    calculateStats(res);
    calculateTrends(res);
  }, [data, filterText, fromDate, toDate]);

  // --- Reports ---
  const downloadExcel = () => {
    try {
      // Format dates for Excel compatibility so sorting works (Oldest to Newest)
      // We explicitly convert date strings to Date objects
      const exportData = filteredData.map(row => ({
        id: row.id,
        job_number: row.job_number,
        project: row.project,
        initiator: row.initiator,
        item: row.item,
        description: row.description,
        unit: row.unit,
        demanded_qty: row.demanded_qty,
        supplied_qty: row.supplied_qty,
        priority: row.priority,
        raised_on: row.raised_on ? new Date(row.raised_on) : null,
        required_date: row.required_date ? new Date(row.required_date) : null,
        supplied_date: row.supplied_date ? new Date(row.supplied_date) : null,
        supply_status: row.supply_status,
        timeliness: row.timeliness,
        construction_remarks: row.construction_remarks,
        procurement_remarks: row.procurement_remarks
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);

      // Optional: Set column widths for better readability
      const wscols = [
        { wch: 15 }, // ID
        { wch: 15 }, // Job #
        { wch: 20 }, // Project
        { wch: 15 }, // Initiator
        { wch: 20 }, // Item
        { wch: 30 }, // Description
        { wch: 10 }, // Unit
        { wch: 12 }, // Demanded
        { wch: 12 }, // Supplied
        { wch: 10 }, // Priority
        { wch: 15 }, // Demand Date
        { wch: 15 }, // Due Date
        { wch: 15 }, // Supplied Date
        { wch: 15 }, // Status
        { wch: 15 }, // Timeliness
        { wch: 20 }, // Const Remarks
        { wch: 20 }, // Proc Remarks
      ];
      ws['!cols'] = wscols;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "D&S Register");
      XLSX.writeFile(wb, "BodlaBuilders_DS_Report.xlsx");
    } catch (e) {
      alert("Excel export failed: " + e.message);
    }
  };

  const downloadPDF = () => {
    try {
      const doc = new jsPDF('l', 'pt'); // Landscape

      // Header
      doc.setFontSize(18);
      doc.text("Bodla Builders Pvt Ltd", 40, 40);
      doc.setFontSize(12);
      doc.text("Demand & Supply Report", 40, 60);
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 40, 80);

      // Simple table dump
      const headers = [["ID", "Project", "Initiator", "Item", "Desc", "Unit", "Qty", "Priority", "Demand Date", "Required", "Status", "Supplied", "Timeliness"]];
      const rows = filteredData.map(d => [
        d.id,
        d.project,
        d.initiator,
        d.item,
        d.description,
        d.unit,
        d.demanded_qty,
        d.priority,
        d.raised_on || '-',
        d.required_date,
        d.supply_status,
        d.supplied_qty,
        d.timeliness
      ]);

      autoTable(doc, {
        head: headers,
        body: rows,
        startY: 100,
        theme: 'grid',
        styles: { fontSize: 8 },
        headStyles: { fillColor: [41, 128, 185] }
      });
      doc.save("BodlaBuilders_DS_Report.pdf");
    } catch (e) {
      console.error(e);
      alert("PDF export failed. See console.");
    }
  };

  // --- Form Handling ---
  const handleOpenSlipSupply = (row) => {
    try {
      const rawSlipId = row.manual_id || (row.id && String(row.id).includes('-') ? String(row.id).split('-')[0] : row.id);
      const slipIdStr = String(rawSlipId || '');

      let slipItems = [];
      if (slipIdStr) {
        slipItems = (data || []).filter(d => 
          (d.manual_id && String(d.manual_id) === slipIdStr) || 
          (d.id && (String(d.id) === slipIdStr || String(d.id).startsWith(`${slipIdStr}-`)))
        );
      }

      const itemsToUpdate = (slipItems && slipItems.length > 0) ? slipItems : [row];
      const initialDate = row.supplied_date || new Date().toISOString().split('T')[0];
      const initialRemarks = row.procurement_remarks || '';

      setSlipBatchDate(initialDate);
      setSlipBatchRemarks(initialRemarks);
      setSelectedSlip({
        slipId: slipIdStr || row.id || 'N/A',
        project: row.project || 'General',
        job_number: row.job_number || '',
        priority: row.priority || 'Medium',
        raised_on: row.raised_on || '',
        required_date: row.required_date || '',
        initiator: row.initiator || '',
        construction_remarks: row.construction_remarks || '',
        items: itemsToUpdate.map(it => ({
          id: it.id,
          item: it.item || 'Item',
          unit: it.unit || '',
          demanded_qty: Number(it.demanded_qty || 0),
          supplied_qty: Number(it.supplied_qty || 0),
          description: it.description || '',
          procurement_remarks: it.procurement_remarks || '',
          supplied_date: it.supplied_date || initialDate,
          supply_status: it.supply_status || 'Pending'
        }))
      });
      setIsSlipModalOpen(true);
    } catch (err) {
      console.error("Error opening slip supply:", err);
      alert("Could not open slip: " + err.message);
    }
  };

  const handleMarkAllFullSupply = () => {
    if (!selectedSlip) return;
    setSelectedSlip(prev => ({
      ...prev,
      items: prev.items.map(it => ({
        ...it,
        supplied_qty: it.demanded_qty,
        supply_status: 'Complete'
      }))
    }));
  };

  const handleClearAllSupply = () => {
    if (!selectedSlip) return;
    setSelectedSlip(prev => ({
      ...prev,
      items: prev.items.map(it => ({
        ...it,
        supplied_qty: 0,
        supply_status: 'Pending'
      }))
    }));
  };

  const handleItemSupplyChange = (index, field, value) => {
    if (!selectedSlip) return;
    const newItems = [...selectedSlip.items];
    newItems[index] = { ...newItems[index], [field]: value };

    if (field === 'supplied_qty') {
      const numVal = Number(value || 0);
      const demVal = Number(newItems[index].demanded_qty || 0);
      if (numVal >= demVal && demVal > 0) {
        newItems[index].supply_status = 'Complete';
      } else if (numVal > 0) {
        newItems[index].supply_status = 'Partially Supplied';
      } else {
        newItems[index].supply_status = 'Pending';
      }
    }
    setSelectedSlip(prev => ({ ...prev, items: newItems }));
  };

  const handleSaveSlipSupplies = async (e) => {
    e.preventDefault();
    if (!selectedSlip) return;
    setLoading(true);
    try {
      const updates = selectedSlip.items.map(it => ({
        id: it.id,
        supplied_qty: Number(it.supplied_qty || 0),
        supplied_date: it.supplied_date || slipBatchDate,
        procurement_remarks: it.procurement_remarks || slipBatchRemarks
      }));

      await axios.put('/api/supply/batch', {
        updates,
        updated_by: user.name,
        slip_remarks: slipBatchRemarks,
        slip_date: slipBatchDate
      });

      alert(`✅ Successfully updated all ${updates.length} items for Slip #${selectedSlip.slipId}!`);
      setIsSlipModalOpen(false);
      setSelectedSlip(null);
      fetchData();
    } catch (err) {
      alert("Failed to save slip supplies: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenNewDemand = () => {
    setEditingItem(null);
    setDemandHeader({
      id: '',
      job_number: '',
      project: '',
      priority: 'Medium',
      raised_on: new Date().toISOString().split('T')[0],
      required_date: '',
      construction_remarks: ''
    });
    setDemandItems([{ item: '', unit: '', demanded_qty: '', description: '' }]);
    setModalOpen(true);
  };

  const handleDuplicate = (row) => {
    // Find all items associated with this slip ID or group
    const targetSlipId = row.manual_id || (row.id && row.id.includes('-') ? row.id.split('-')[0] : row.id);
    const relatedItems = data.filter(d => (d.manual_id && d.manual_id === targetSlipId) || (d.id && (d.id === targetSlipId || d.id.startsWith(`${targetSlipId}-`))));
    
    const itemsToClone = relatedItems.length > 0 ? relatedItems : [row];

    setEditingItem(null); // Creation mode
    setDemandHeader({
      id: '', // Blank so a new slip ID can be assigned or auto-generated
      job_number: row.job_number || '',
      project: row.project || '',
      priority: row.priority || 'Medium',
      raised_on: new Date().toISOString().split('T')[0], // Defaults to today for the new cloned demand
      required_date: row.required_date || '',
      construction_remarks: row.construction_remarks || ''
    });

    setDemandItems(itemsToClone.map(it => ({
      item: it.item || '',
      unit: it.unit || '',
      demanded_qty: it.demanded_qty || '',
      description: it.description || ''
    })));

    setModalOpen(true);
  };

  const handleAddItem = () => {
    setDemandItems([...demandItems, { item: '', unit: '', demanded_qty: '', description: '' }]);
  };

  const handleRemoveItem = (index) => {
    const newItems = [...demandItems];
    newItems.splice(index, 1);
    setDemandItems(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData(form);
    const payload = Object.fromEntries(formData.entries());

    setLoading(true);
    try {
      const isUpdate = !!editingItem;

      if (!navigator.onLine) {
        const offlineReqs = JSON.parse(localStorage.getItem('offlineRequests') || '[]');
        if (!isUpdate) {
          const header = {
            id: demandHeader.id || payload.id,
            job_number: demandHeader.job_number || payload.job_number,
            project: demandHeader.project || payload.project,
            priority: demandHeader.priority || payload.priority,
            raised_on: demandHeader.raised_on || payload.raised_on || new Date().toISOString().split('T')[0],
            required_date: demandHeader.required_date || payload.required_date,
            initiator: user.name,
            remarks: demandHeader.construction_remarks || payload.construction_remarks
          };
          const validItems = demandItems.filter(i => i.item && i.demanded_qty);
          if (validItems.length === 0) throw new Error("Please add at least one item.");
          offlineReqs.push({ type: 'create', payload: { header, items: validItems } });
        } else {
          offlineReqs.push({
            type: 'update', id: editingItem.id, payload: {
              ...payload,
              raised_on: payload.raised_on || editingItem.raised_on || new Date().toISOString().split('T')[0],
              demanded_qty: Number(payload.demanded_qty || editingItem.demanded_qty),
              supplied_qty: Number(payload.supplied_qty || 0),
              supplied_date: payload.supplied_date || new Date().toISOString().split('T')[0],
              updated_by: user.name
            }
          });
        }
        localStorage.setItem('offlineRequests', JSON.stringify(offlineReqs));
        alert("📡 No Internet Connection! Your update has been securely saved locally. It will auto-sync when you regain internet.");
        setModalOpen(false);
        setEditingItem(null);
        setDemandItems([{ item: '', unit: '', demanded_qty: '', description: '' }]);
        return;
      }

      if (!isUpdate) {
        // Multi-Item Create
        const header = {
          id: demandHeader.id || payload.id,
          job_number: demandHeader.job_number || payload.job_number,
          project: demandHeader.project || payload.project,
          priority: demandHeader.priority || payload.priority,
          raised_on: demandHeader.raised_on || payload.raised_on || new Date().toISOString().split('T')[0],
          required_date: demandHeader.required_date || payload.required_date,
          initiator: user.name,
          remarks: demandHeader.construction_remarks || payload.construction_remarks
        };
        const validItems = demandItems.filter(i => i.item && i.demanded_qty);
        if (validItems.length === 0) throw new Error("Please add at least one item.");

        await axios.post('/api/demand', { header, items: validItems });
      } else {
        // Update Existing
        await axios.put(`/api/supply/${editingItem.id}`, {
          ...payload,
          raised_on: payload.raised_on || editingItem.raised_on || new Date().toISOString().split('T')[0],
          demanded_qty: Number(payload.demanded_qty || editingItem.demanded_qty),
          supplied_qty: Number(payload.supplied_qty || 0),
          supplied_date: payload.supplied_date || new Date().toISOString().split('T')[0],
          updated_by: user.name
        });
      }
      setModalOpen(false);
      setEditingItem(null);
      setDemandItems([{ item: '', unit: '', demanded_qty: '', description: '' }]);
      fetchData();
      alert("✅ Update Successful!");
    } catch (error) {
      alert("Failed to save. " + error.message);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this record? This action cannot be undone.")) {
      try {
        await axios.delete(`/api/demand/${id}`);
        alert("🗑️ Record Deleted Successfully");
        fetchData();
      } catch (error) {
        console.error(error);
        alert("Failed to delete record.");
      }
    }
  };


  const handleDisconnect = async () => {
    if (!window.confirm("Are you sure you want to disconnect WhatsApp binding? This will stop alerts until reconnected.")) return;
    try {
      setWaStatus({ status: 'Disconnecting...', qr: null });
      await axios.post('/api/whatsapp/logout');
      // Will be re-fetched by polling in useEffect
      alert("✅ Disconnected. Wait for new QR code.");
    } catch (err) {
      alert("Failed to disconnect: " + err.message);
    }
  };

  // --- UI Helpers & Role Validation ---
  const userRole = String(user?.role || '').toLowerCase().trim();
  const userName = String(user?.name || '').toLowerCase().trim();
  const isProcurementOrAdmin = userRole.includes('procurement') || userRole.includes('supply') || userRole.includes('admin') || userName.includes('supply') || userName.includes('admin');
  const isConstructionOrAdmin = userRole.includes('construction') || userRole.includes('site') || userRole.includes('admin') || userName.includes('site') || userName.includes('admin');
  const isAdmin = userRole === 'admin' || userName === 'admin';

  const getStatusColor = (status) => {
    if (status === 'Pending') return 'bg-yellow-100 text-yellow-800';
    if (status === 'Partially Supplied') return 'bg-orange-100 text-orange-800';
    if (status === 'Supplied' || status === 'Complete') return 'bg-green-100 text-green-800';
    if (status === 'Delayed') return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  }

  const chartData1 = [
    { name: 'Demanded', value: stats.demanded },
    { name: 'Supplied', value: stats.supplied },
  ];

  const chartData2 = [
    { name: 'Delayed', value: stats.delayed, color: '#ef4444' },
    { name: 'Pending', value: stats.pending, color: '#eab308' },
    { name: 'Partial', value: stats.partially_supplied, color: '#f97316' },
    { name: 'Completed', value: stats.supplied, color: '#10b981' },
  ].filter(i => i.value > 0);

  // Clean, professional premium card style
  const cardClass = "bg-white/80 backdrop-blur-md p-6 rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all duration-300 relative overflow-hidden";
  const miniCardClass = "p-4 rounded-[1.5rem] shadow-sm flex flex-col items-center justify-center gap-1 transition-all active:scale-95 text-center border";

  return (
    <div className="min-h-screen bg-[#f8fafc] text-gray-800 font-sans selection:bg-blue-100 pb-20 md:pb-0">
      {/* Navbar - Premium Glass Gradient (Hidden on Mobile Tabs except Home) */}
      <nav className={`bg-gradient-to-r from-[#0f172a] to-[#1e3a8a] border-b border-blue-900 sticky top-0 z-40 px-6 py-4 flex justify-between items-center shadow-lg backdrop-blur-md ${activeTab !== 'home' ? 'hidden md:flex' : 'flex'}`}>
        <div className="flex items-center gap-3">
          <div className="bg-white p-1 rounded-xl shadow-inner">
            <img src={logo} alt="Bodla Builders Logo" className="h-10 w-auto object-contain rounded-lg" />
          </div>
          <div className="ml-2">
            <h1 className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-100 to-white tracking-tight">Bodla D&S Dashboard</h1>
            <p className="text-[10px] text-blue-300 font-bold uppercase tracking-[0.2em]">Bodla Builders Pvt Ltd</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* Real WhatsApp Status Indicator */}
          {/* Real WhatsApp Status Indicator - Admin Only */}
          {user.role === 'Admin' && (
            <button
              onClick={() => setShowQrModal(true)}
              className={`hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full border transition-all ${waStatus.status === 'CONNECTED' ? 'bg-green-50 border-green-200 text-green-700' :
                waStatus.status === 'QR_READY' ? 'bg-yellow-50 border-yellow-200 text-yellow-700 animate-pulse' :
                  ['INITIALIZING', 'AUTHENTICATED', 'DISCONNECTING'].includes(waStatus.status) ? 'bg-blue-50 border-blue-200 text-blue-700 animate-pulse' :
                    'bg-red-50 border-red-200 text-red-700'
                }`}
            >
              <MessageCircle size={16} />
              <span className="text-xs font-semibold">
                {waStatus.status === 'CONNECTED' ? 'WhatsApp Connected' :
                  waStatus.status === 'QR_READY' ? 'Scan QR Code' :
                    waStatus.status === 'INITIALIZING' ? 'Initializing...' :
                      waStatus.status === 'AUTHENTICATED' ? 'Authenticating...' :
                        waStatus.status === 'DISCONNECTING' ? 'Resetting...' :
                          (waStatus.status && waStatus.status.startsWith('Loading')) ? waStatus.status :
                            'Disconnected'}
              </span>
              {waStatus.status === 'CONNECTED' && (
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
              )}
            </button>
          )}

          <div className="hidden md:block text-right border-r border-blue-800/50 pr-5 mr-3">
            <p className="text-sm font-bold text-white tracking-wide">{user.name}</p>
            <p className="text-xs text-blue-300 font-semibold tracking-wider uppercase">{user.role}</p>
          </div>
          {user.role === 'Admin' && (
            <button onClick={() => setShowUserModal(true)} className="p-2 text-blue-200 hover:text-white hover:bg-white/10 rounded-xl transition-all" title="Manage Users">
              <Users size={20} />
            </button>
          )}
          <button onClick={downloadExcel} className="p-2 text-blue-200 hover:text-green-300 hover:bg-white/10 rounded-xl transition-all" title="Export Excel">
            <FileSpreadsheet size={20} />
          </button>
          <button onClick={downloadPDF} className="p-2 text-blue-200 hover:text-red-300 hover:bg-white/10 rounded-xl transition-all" title="Export PDF">
            <FileText size={20} />
          </button>
          {onBackToPortal && (
            <button 
              onClick={onBackToPortal} 
              className="px-5 py-2 text-sm font-bold text-bodla-gold hover:text-white bg-white/5 hover:bg-bodla-gold border border-bodla-gold/30 rounded-xl transition-all shadow-md transform hover:scale-105 active:scale-95"
            >
              ← Portal
            </button>
          )}
          <button onClick={onLogout} className="px-5 py-2 text-sm font-bold text-blue-900 bg-white hover:bg-gray-100 rounded-xl transition-all shadow-md transform hover:scale-105 active:scale-95">
            Logout
          </button>
        </div>
      </nav>

      {/* QR Code Modal (Enhanced) */}
      {showQrModal && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 flex flex-col items-center w-full max-w-sm">
            <div className="flex justify-between w-full mb-4">
              <h3 className="text-lg font-bold">WhatsApp Connection (v5.0 Final)</h3>
              <button onClick={() => setShowQrModal(false)}><X size={20} className="text-gray-400 hover:text-gray-600" /></button>
            </div>

            {/* Config Section */}
            <div className="w-full mb-4 space-y-2 bg-gray-50 p-3 rounded-lg border border-gray-200">
              <label className="block text-xs font-bold text-gray-700 uppercase">Target WhatsApp Group Name</label>
              <input
                value={waConfig.groupName}
                onChange={e => setWaConfig({ ...waConfig, groupName: e.target.value })}
                className="w-full text-xs p-2 border rounded focus:ring-1 focus:ring-blue-500 outline-none"
                placeholder="e.g. BB-Demand & Supply"
              />

              <label className="block text-xs font-bold text-gray-700 uppercase mt-2">Group Invite Link</label>
              <input
                value={waConfig.inviteLink}
                onChange={e => setWaConfig({ ...waConfig, inviteLink: e.target.value })}
                className="w-full text-xs p-2 border rounded focus:ring-1 focus:ring-blue-500 outline-none"
                placeholder="https://chat.whatsapp.com/..."
              />

              <button
                onClick={async () => {
                  try {
                    await axios.post('/api/whatsapp/config', waConfig);
                    alert("✅ Configuration Saved!");
                  } catch (e) { alert("Save failed"); }
                }}
                className="w-full mt-2 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded shadow-sm transition-all"
              >
                Save Configuration
              </button>
            </div>

            {waStatus.status === 'CONNECTED' ? (
              <div className="text-center w-full py-4 space-y-4">
                <div className="flex flex-col items-center justify-center text-green-600">
                  <CheckCircle size={64} className="text-green-500 mb-2" />
                  <p className="font-bold text-lg text-gray-800">Successfully Connected!</p>
                </div>

                {waStatus.user && (
                  <div className="bg-green-50 p-3 rounded-lg border border-green-100 text-sm w-full text-left">
                    <p className="text-xs text-green-600 uppercase font-bold tracking-wider mb-1">Active Account:</p>
                    <p className="font-semibold text-green-900 text-base">{waStatus.user.name || 'WhatsApp User'}</p>
                    <p className="font-mono text-green-700">+{waStatus.user.number}</p>
                  </div>
                )}

                <div className="pt-2 border-t border-gray-100">
                  <p className="text-xs text-gray-400 mb-3">To switch accounts, disconnect first:</p>
                  <button
                    onClick={handleDisconnect}
                    className="w-full py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg shadow-md hover:shadow-lg transition-all text-sm font-bold uppercase tracking-wide flex items-center justify-center gap-2"
                  >
                    Disconnect & Rebind Number
                  </button>
                  <p className="text-[10px] text-gray-300 mt-2">v5.0 Admin Control Active</p>
                </div>
              </div>
            ) : waStatus.status === 'QR_READY' && waStatus.qr ? (
              <div className="text-center">
                <p className="text-sm text-gray-500 mb-4 bg-yellow-50 p-2 rounded text-yellow-800 border border-yellow-100">
                  ⚠️ Scan closely. Code changes every 15s.
                </p>
                <div className="p-2 border-2 border-gray-100 rounded-lg inline-block bg-white">
                  <img src={waStatus.qr} alt="WA QR Code" className="w-64 h-64 object-contain" />
                </div>
                <p className="text-xs text-gray-400 mt-3">Open WhatsApp &gt; Settings &gt; Linked Devices</p>
              </div>
            ) : (
              <div className="text-center py-12 w-full">
                <div className="animate-spin rounded-full h-12 w-12 border-[3px] border-blue-100 border-t-blue-600 mx-auto mb-6"></div>
                <h4 className="font-bold text-gray-800 mb-1">
                  {waStatus.status === 'INITIALIZING' ? 'Starting Server...' :
                    waStatus.status === 'AUTHENTICATED' ? 'Authenticating Session...' :
                      waStatus.status === 'DISCONNECTING' ? 'Resetting Connection...' :
                        (waStatus.status && waStatus.status.startsWith('Loading')) ? waStatus.status :
                          'Please Wait...'}
                </h4>
                <p className="text-sm text-gray-400">
                  {waStatus.status === 'INITIALIZING' ? 'This normally takes 10-20 seconds.' :
                    waStatus.status === 'AUTHENTICATED' ? 'Syncing messages (do not close)...' :
                      (waStatus.status && waStatus.status.startsWith('Loading')) ? 'Syncing WhatsApp data...' :
                        'Connecting to WhatsApp services...'}
                </p>
              </div>
            )}

            <button onClick={() => setShowQrModal(false)} className="mt-6 text-gray-400 hover:text-gray-800 text-xs font-medium uppercase tracking-wider">Close Window</button>
          </div>
        </div>
      )}

      <main className="w-full px-6 py-6 space-y-6">
        {/* --- MOBILE VIEW: TAB-BASED NAVIGATION --- */}
        <div className="md:hidden">
          {activeTab === 'home' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="flex flex-col gap-1">
                <h2 className="text-2xl font-black text-gray-900">Demand & Supply Hub</h2>
                <p className="text-sm text-gray-500 font-medium">Syncing site requirements with precision logistics</p>
              </div>

              {/* Colorful KPI Grid inspired by Reference */}
              <div className="grid grid-cols-2 gap-4">
                <div className={`${miniCardClass} bg-blue-600 border-blue-500 text-white`}>
                  <TrendingUp size={20} className="mb-1 opacity-80" />
                  <span className="text-2xl font-black">{stats.demanded}</span>
                  <span className="text-[10px] uppercase font-bold tracking-wider opacity-70">Total Demands</span>
                </div>
                <div className={`${miniCardClass} bg-emerald-500 border-emerald-400 text-white`}>
                  <CheckCircle size={20} className="mb-1 opacity-80" />
                  <span className="text-2xl font-black">{stats.supplied}</span>
                  <span className="text-[10px] uppercase font-bold tracking-wider opacity-70">Supplies</span>
                </div>
                <div className={`${miniCardClass} bg-amber-400 border-amber-300 text-white`}>
                  <AlertTriangle size={20} className="mb-1 opacity-80" />
                  <span className="text-2xl font-black">{stats.pending}</span>
                  <span className="text-[10px] uppercase font-bold tracking-wider opacity-70">Pending</span>
                </div>
                <div className={`${miniCardClass} bg-rose-500 border-rose-400 text-white`}>
                  <TrendingUp size={20} className="mb-1 rotate-90 opacity-80" />
                  <span className="text-2xl font-black">{stats.delayed}</span>
                  <span className="text-[10px] uppercase font-bold tracking-wider opacity-70">Delayed Alerts</span>
                </div>
              </div>

              {/* Recent Activity List (Mobile Optimized) - restored for visibility */}
              <div className="pt-2">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-sm font-bold text-gray-800 uppercase tracking-widest">Recent Demands</h3>
                  <button onClick={() => setActiveTab('register')} className="text-blue-600 font-bold text-xs">View All</button>
                </div>
                <div className="space-y-3">
                  {filteredData.slice(0, 5).map((row) => (
                    <div key={row.id} onClick={() => { if (user.role === 'Procurement' || user.role === 'Admin') { handleOpenSlipSupply(row); } }} className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between active:scale-[0.98] transition-all cursor-pointer">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${row.supply_status === 'Complete' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'}`}>
                          {row.supply_status === 'Complete' ? <CheckCircle size={18} /> : <FileText size={18} />}
                        </div>
                        <div className="flex flex-col">
                          <span className="font-bold text-gray-900 text-sm truncate max-w-[120px]">{row.item}</span>
                          <span className="text-[10px] text-gray-500 font-bold uppercase">{row.project}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="block font-black text-gray-900 text-sm">{row.demanded_qty}</span>
                        <div className={`text-[8px] px-1.5 py-0.5 rounded font-black uppercase ${getStatusColor(row.supply_status)}`}>{row.supply_status}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Action Button - Floating Style */}
              {(user.role === 'Construction' || user.role === 'Admin') && (
                <div className="pt-4">
                  <button
                    onClick={() => { setEditingItem(null); setModalOpen(true); }}
                    className="w-full py-6 bg-gradient-to-r from-blue-600 to-[#1e3a8a] text-white font-black rounded-[2rem] shadow-2xl shadow-blue-500/40 flex flex-col items-center justify-center gap-2 active:scale-[0.95] transition-all border-t border-white/20"
                  >
                    <Plus size={32} className="bg-white/20 p-1.5 rounded-full" />
                    <span className="text-lg tracking-tight">RAISE NEW DEMAND</span>
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-500 pb-10">
              <h2 className="text-2xl font-black text-gray-900 mb-2">Detailed Analytics</h2>



              {/* Demand vs Supply Trend - RESTORED HERE */}
              <div className={cardClass}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                    <TrendingUp size={18} className="text-blue-600" />
                    Demand vs Supply Trend (Daily)
                  </h3>
                  <div className="flex gap-4 text-[10px] font-bold">
                    <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500"></span> Demand</div>
                    <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500"></span> Supply</div>
                  </div>
                </div>
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trendData} margin={{ top: 20, right: 30, left: 10, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis
                        dataKey="date"
                        axisLine={false}
                        tickLine={false}
                        style={{ fontSize: '10px', fontWeight: 'bold', fill: '#94a3b8' }}
                        dy={10}
                      />
                      <YAxis hide domain={['auto', 'auto']} />
                      <Tooltip
                        contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)', fontSize: '12px' }}
                      />
                      <Legend verticalAlign="top" align="right" height={36} iconType="circle" wrapperStyle={{ fontSize: '10px', fontWeight: 'bold' }} />

                      {/* Demand Line - Blue */}
                      <Line
                        name="Demand"
                        type="monotone"
                        dataKey="demand"
                        stroke="#3b82f6"
                        strokeWidth={3}
                        dot={{ r: 4, fill: '#3b82f6', strokeWidth: 2, stroke: '#fff' }}
                        activeDot={{ r: 6 }}
                      >
                        <LabelList dataKey="demand" position="top" style={{ fill: '#1d4ed8', fontSize: '11px', fontWeight: '900' }} />
                      </Line>

                      {/* Supply Line - Green */}
                      <Line
                        name="Supply"
                        type="monotone"
                        dataKey="supply"
                        stroke="#10b981"
                        strokeWidth={3}
                        dot={{ r: 4, fill: '#10b981', strokeWidth: 2, stroke: '#fff' }}
                        activeDot={{ r: 6 }}
                      >
                        <LabelList dataKey="supply" position="bottom" offset={10} style={{ fill: '#065f46', fontSize: '11px', fontWeight: '900' }} />
                      </Line>
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className={cardClass}>
                <h3 className="text-sm font-bold text-gray-800 mb-4">Volume Statistics</h3>
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData1} margin={{ top: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} style={{ fontSize: '10px' }} />
                      <YAxis axisLine={false} tickLine={false} hide />
                      <Bar dataKey="value" fill="#1e3a8a" radius={[10, 10, 0, 0]}>
                        <LabelList dataKey="value" position="top" style={{ fill: '#1e3a8a', fontSize: '12px', fontWeight: 'bold' }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className={cardClass}>
                <h3 className="text-sm font-bold text-gray-800 mb-4">Overall Completion</h3>
                <div className="h-64 w-full flex items-center justify-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData2}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={75}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {chartData2.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }} />
                      <Legend verticalAlign="bottom" height={45} iconType="circle" wrapperStyle={{ fontSize: '10px', fontWeight: 'bold', paddingTop: '10px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'register' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-10">
              <div className="flex flex-col gap-2">
                <h2 className="text-2xl font-black text-gray-900">Live Action Register</h2>
                <p className="text-sm text-gray-500 font-medium tracking-tight">Tap any item to update supply details</p>
              </div>

              {/* Mobile Filter & Duration Bar */}
              <div className="bg-white p-4 rounded-[1.5rem] shadow-sm border border-gray-100 flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <Search className="text-gray-400" size={18} />
                  <input
                    className="w-full text-sm outline-none text-gray-700 bg-transparent placeholder-gray-400 font-medium"
                    placeholder="Search project, item, job#, or status..."
                    value={filterText}
                    onChange={e => setFilterText(e.target.value)}
                  />
                  {filterText && <button onClick={() => setFilterText('')}><X size={16} className="text-gray-400" /></button>}
                </div>
                <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-100">
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] font-bold text-gray-400 uppercase">From:</span>
                    <input
                      type="date"
                      value={fromDate}
                      onChange={e => setFromDate(e.target.value)}
                      className="text-[11px] p-1 border border-gray-200 rounded-md outline-none bg-gray-50 font-medium"
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] font-bold text-gray-400 uppercase">To:</span>
                    <input
                      type="date"
                      value={toDate}
                      onChange={e => setToDate(e.target.value)}
                      className="text-[11px] p-1 border border-gray-200 rounded-md outline-none bg-gray-50 font-medium"
                    />
                  </div>
                  {(fromDate || toDate) && (
                    <button onClick={() => { setFromDate(''); setToDate(''); }} className="text-[10px] text-red-500 font-bold px-1.5 py-0.5 bg-red-50 rounded">Clear</button>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-4 pb-24">
                {filteredData.slice(0, 50).map((row) => (
                  <div key={row.id} onClick={() => { if (user.role === 'Procurement' || user.role === 'Admin') { handleOpenSlipSupply(row); } }} className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-lg flex flex-col gap-4 active:scale-[0.98] transition-all">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-3 rounded-2xl ${row.supply_status === 'Complete' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'}`}>
                          {row.supply_status === 'Complete' ? <CheckCircle size={22} /> : <FileText size={22} />}
                        </div>
                        <div className="flex flex-col">
                          <span className="font-black text-gray-900 text-lg leading-tight">{row.item}</span>
                          <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">{row.project}</span>
                        </div>
                      </div>
                      <div className={`text-[10px] px-2 py-1 rounded-full font-black uppercase ${row.priority === 'High' ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}>
                        {row.priority}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-50">
                      <div>
                        <span className="text-[10px] text-gray-400 font-bold uppercase block">Demanded Qty</span>
                        <span className="font-black text-gray-900">{row.demanded_qty} {row.unit}</span>
                      </div>
                      <div>
                        <span className="text-[10px] text-gray-400 font-bold uppercase block">Status</span>
                        <span className={`font-black ${getStatusColor(row.supply_status).split(' ')[1]}`}>{row.supply_status}</span>
                      </div>
                    </div>

                    <div className="flex justify-between items-center text-[10px] font-bold text-gray-400 uppercase tracking-widest pt-2 border-t border-gray-50">
                      <span>Job: {row.job_number || 'N/A'}</span>
                      <div className="flex items-center gap-2">
                        {(user.role === 'Procurement' || user.role === 'Admin') && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleOpenSlipSupply(row); }}
                            className="text-emerald-700 hover:text-emerald-800 bg-emerald-50 px-2.5 py-1 rounded-lg text-xs font-bold flex items-center gap-1 border border-emerald-200"
                            title="Update Slip Supply"
                          >
                            <Truck size={12} /> Update
                          </button>
                        )}
                        {(user.role === 'Construction' || user.role === 'Admin') && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleDuplicate(row); }}
                            className="text-indigo-600 hover:text-indigo-800 bg-indigo-50 px-2.5 py-1 rounded-lg text-xs font-bold flex items-center gap-1 border border-indigo-100"
                            title="Duplicate this demand"
                          >
                            <Copy size={12} /> Copy
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-12">
              <h2 className="text-2xl font-black text-gray-900">Security & Apps</h2>
              <div className={cardClass}>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-blue-900 rounded-2xl flex items-center justify-center text-white text-2xl font-black">{user.name[0]}</div>
                  <div>
                    <h4 className="font-black text-lg text-gray-900">{user.name}</h4>
                    <p className="text-xs text-blue-600 font-bold uppercase tracking-wider">{user.role} Manager</p>
                  </div>
                </div>

                <div className="space-y-3">
                  {user.role === 'Admin' && (
                    <button onClick={() => setShowQrModal(true)} className="w-full p-4 bg-gray-50 hover:bg-gray-100 rounded-2xl border border-gray-200 flex items-center justify-between transition-all">
                      <div className="flex items-center gap-3 text-gray-700 font-bold"><MessageCircle size={20} className="text-green-600" /> WhatsApp Control</div>
                      <CheckCircle size={18} className="text-green-500" />
                    </button>
                  )}
                  <button onClick={downloadExcel} className="w-full p-4 bg-gray-50 hover:bg-gray-100 rounded-2xl border border-gray-200 flex items-center gap-3 text-gray-700 font-bold">
                    <FileSpreadsheet size={20} className="text-emerald-600" /> Export All Data (Excel)
                  </button>
                  <button onClick={onLogout} className="w-full p-4 bg-red-50 hover:bg-red-100 rounded-2xl border border-red-100 flex items-center gap-3 text-red-700 font-bold mt-4">
                    <X size={20} /> Secure Logout
                  </button>
                </div>
              </div>
              <p className="text-center text-gray-400 text-xs font-bold uppercase tracking-[0.3em]">Bodla Builders v6.0</p>
            </div>
          )}
        </div>

        {/* --- DESKTOP VIEW: ORIGINAL HIGH-POWER DASHBOARD --- */}
        <div className="hidden md:block space-y-6">
          {/* Global Filter & Duration Selection */}
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 flex-1 min-w-[280px]">
              <Search className="text-gray-400" size={20} />
              <input
                className="w-full text-sm outline-none text-gray-700 bg-transparent placeholder-gray-400"
                placeholder="Filter by Project, ID, Item, Job#, or Status..."
                value={filterText}
                onChange={e => setFilterText(e.target.value)}
              />
              {filterText && <button onClick={() => setFilterText('')}><X size={16} className="text-gray-400 hover:text-red-500" /></button>}
            </div>

            {/* Calendar Duration Selection */}
            <div className="flex items-center gap-3 border-l border-gray-200 pl-4 py-1">
              <div className="flex items-center gap-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase">From:</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={e => setFromDate(e.target.value)}
                  className="text-xs p-1.5 border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 bg-gray-50 text-gray-700 font-medium cursor-pointer"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase">To:</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={e => setToDate(e.target.value)}
                  className="text-xs p-1.5 border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 bg-gray-50 text-gray-700 font-medium cursor-pointer"
                />
              </div>
              {(fromDate || toDate) && (
                <button
                  onClick={() => { setFromDate(''); setToDate(''); }}
                  className="text-xs text-red-500 hover:text-red-700 font-bold px-2 py-1 bg-red-50 rounded-lg transition-all"
                  title="Clear Date Range"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className={cardClass}>
              <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Total Demands</p>
              <div className="text-3xl font-bold text-gray-900 mt-2">{stats.demanded}</div>
              <div className="w-full bg-gray-100 rounded-full h-1.5 mt-3">
                <div className="bg-blue-600 h-1.5 rounded-full" style={{ width: '100%' }}></div>
              </div>
            </div>
            <div className={cardClass}>
              <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Supplied Complete</p>
              <div className="text-3xl font-bold text-blue-600 mt-2">{stats.supplied}</div>
              <div className="w-full bg-gray-100 rounded-full h-1.5 mt-3">
                <div className="bg-blue-400 h-1.5 rounded-full" style={{ width: `${(stats.supplied / (stats.demanded || 1)) * 100}%` }}></div>
              </div>
            </div>
            <div className={cardClass}>
              <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Pending Actions</p>
              <div className="text-3xl font-bold text-yellow-600 mt-2">{stats.pending}</div>
              <div className="w-full bg-gray-100 rounded-full h-1.5 mt-3">
                <div className="bg-yellow-400 h-1.5 rounded-full" style={{ width: `${(stats.pending / (stats.demanded || 1)) * 100}%` }}></div>
              </div>
            </div>
            <div className={cardClass}>
              <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Delayed Items</p>
              <div className="text-3xl font-bold text-red-600 mt-2">{stats.delayed}</div>
              <div className="w-full bg-gray-100 rounded-full h-1.5 mt-3">
                <div className="bg-red-400 h-1.5 rounded-full" style={{ width: `${(stats.delayed / (stats.demanded || 1)) * 100}%` }}></div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className={cardClass}>
              <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                <span className="w-1.5 h-6 bg-blue-900 rounded-full"></span> Demand/Supply Vol
              </h3>
              <div className="h-48 md:h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData1} barSize={40}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#6b7280', fontSize: 11 }} dy={10} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#6b7280', fontSize: 11 }} />
                    <Tooltip cursor={{ fill: '#f9fafb' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                    <Bar dataKey="value" fill="#1e3a8a" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className={cardClass}>
              <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                <TrendingUp size={18} className="text-blue-600" />
                Trends (Last 10 Days)
              </h3>
              <div className="h-48 md:h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                    <XAxis
                      dataKey="date"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#6b7280', fontSize: 10 }}
                      dy={10}
                      tickFormatter={(str) => {
                        const d = new Date(str);
                        return `${d.getDate()}/${d.getMonth() + 1}`;
                      }}
                    />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#6b7280', fontSize: 10 }} />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} labelFormatter={(l) => new Date(l).toLocaleDateString()} />
                    <Legend verticalAlign="top" height={36} iconSize={8} wrapperStyle={{ fontSize: '10px' }} />
                    <Line type="monotone" dataKey="demand" name="Demands" stroke="#3b82f6" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} />
                    <Line type="monotone" dataKey="supply" name="Supplies" stroke="#10b981" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className={cardClass}>
              <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                <span className="w-1.5 h-6 bg-orange-500 rounded-full"></span> Status Breakdown
              </h3>
              <div className="h-48 md:h-64 w-full flex items-center justify-center">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={chartData2} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={5} dataKey="value">
                      {chartData2.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} strokeWidth={0} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                    <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ paddingTop: '10px', fontSize: '11px' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

          </div>

          {/* Detailed Register Table (Desktop Only) */}
          <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 flex flex-col">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-white shrink-0">
              <div>
                <h2 className="text-xl font-black text-gray-900 tracking-tight">Master Data Register</h2>
                <p className="text-[13px] text-gray-500 mt-1 font-medium tracking-wide">Real-time enterprise tracking of all project demands</p>
              </div>
              {isConstructionOrAdmin && (
                <button onClick={handleOpenNewDemand} className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 hover:-translate-y-0.5 transition-all font-bold text-sm tracking-wide">
                  <Plus size={18} /> Raise New Demand
                </button>
              )}
            </div>
            {/* Viewport-Friendly Scroll Container: Always shows horizontal scrollbar right at bottom of table box */}
            <div className="overflow-auto max-h-[68vh] relative rounded-b-2xl border-t border-gray-100">
              <table className="w-full text-left border-collapse min-w-[2000px] whitespace-nowrap">
                <thead className="bg-slate-100 text-gray-700 text-xs font-bold uppercase tracking-wider sticky top-0 z-20 shadow-sm border-b border-gray-200">
                  <tr>
                    <th className="p-4 bg-slate-100">ID</th>
                    <th className="p-4 bg-slate-100">Job #</th>
                    <th className="p-4 bg-slate-100">Project</th>
                    <th className="p-4 bg-slate-100">Item Name</th>
                    <th className="p-4 bg-slate-100">Description/Spec</th>
                    <th className="p-4 bg-slate-100 text-center">Unit</th>
                    <th className="p-4 bg-slate-100 text-center">Demanded Qty</th>
                    <th className="p-4 bg-slate-100 text-center">Supplied Qty</th>
                    <th className="p-4 bg-slate-100 text-center">Balance</th>
                    <th className="p-4 bg-slate-100">Priority</th>
                    <th className="p-4 bg-slate-100">Demand Date</th>
                    <th className="p-4 bg-slate-100">Due Date</th>
                    <th className="p-4 bg-slate-100">Supplied Date</th>
                    <th className="p-4 bg-slate-100 text-center">Status</th>
                    <th className="p-4 bg-slate-100">Timeliness</th>
                    <th className="p-4 bg-slate-100">Initiator</th>
                    <th className="p-4 bg-slate-100">Const. Remarks</th>
                    <th className="p-4 bg-slate-100">Proc. Remarks</th>
                    <th className="p-4 bg-slate-100 text-right sticky right-0 shadow-[-10px_0_15px_-3px_rgba(0,0,0,0.05)] z-20">Actions</th>
                  </tr>
                </thead>
                <tbody className="text-sm divide-y divide-gray-50 bg-white">
                  {filteredData.map(row => (
                    <tr key={row.id} className="hover:bg-blue-50/40 transition-colors group">
                      <td className="p-4 font-mono text-gray-400 font-medium">{row.id}</td>
                      <td className="p-4 font-bold text-blue-900">{row.job_number || '-'}</td>
                      <td className="p-4 font-bold text-gray-800">{row.project || "N/A"}</td>
                      <td className="p-4 font-bold text-gray-900">{row.item}</td>
                      <td className="p-4 text-gray-600 max-w-[200px] truncate" title={row.description}>{row.description}</td>
                      <td className="p-4 text-center text-gray-500">{row.unit}</td>
                      <td className="p-4 text-center font-bold">{row.demanded_qty}</td>
                      <td className="p-4 text-center font-medium text-green-600">{row.supplied_qty}</td>
                      <td className="p-4 text-center font-bold text-gray-400">
                        {Math.max(0, row.demanded_qty - row.supplied_qty)}
                      </td>
                      <td className="p-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border
                                          ${row.priority === 'High' ? 'bg-red-50 text-red-700 border-red-100' :
                            row.priority === 'Medium' ? 'bg-yellow-50 text-yellow-700 border-yellow-100' : 'bg-blue-50 text-blue-700 border-blue-100'}`}>
                          {row.priority}
                        </span>
                      </td>
                      <td className="p-4 text-gray-700 font-medium">{row.raised_on || '-'}</td>
                      <td className="p-4 text-gray-700">{row.required_date}</td>
                      <td className="p-4 text-gray-500 italic">{row.supplied_date || "-"}</td>
                      <td className="p-4">
                        <div className={`text-xs font-bold px-2 py-1 rounded w-fit text-center ${getStatusColor(row.supply_status)}`}>
                          {row.supply_status}
                        </div>
                      </td>
                      <td className="p-4">
                        {row.timeliness !== 'Pending' && (
                          <div className={`text-[10px] px-2 py-0.5 rounded border w-fit ${row.timeliness === 'Delayed' ? 'text-red-700 border-red-200 bg-red-50' : 'text-green-700 border-green-200 bg-green-50'}`}>
                            {row.timeliness}
                          </div>
                        )}
                      </td>
                      <td className="p-4 text-gray-500">{row.initiator}</td>
                      <td className="p-4 text-xs text-gray-600 max-w-[150px] truncate" title={row.construction_remarks}>{row.construction_remarks}</td>
                      <td className="p-4 text-xs text-blue-600 max-w-[150px] truncate" title={row.procurement_remarks}>{row.procurement_remarks}</td>
                      <td className="p-4 text-right sticky right-0 bg-white group-hover:bg-blue-50/50 shadow-[-10px_0_15px_-3px_rgba(0,0,0,0.05)] border-l border-gray-100 flex gap-2 justify-end z-10">
                        {isProcurementOrAdmin && (
                          <button
                            onClick={() => handleOpenSlipSupply(row)}
                            className="text-emerald-700 hover:text-emerald-800 bg-emerald-50 hover:bg-emerald-100 px-3 py-1.5 rounded-lg transition-all text-xs font-bold border border-emerald-200 flex items-center gap-1.5 shadow-sm"
                            title="Update Supply for all items in this Demand Slip"
                          >
                            <Truck size={14} /> Update Supply
                          </button>
                        )}
                        {isAdmin && (
                          <>
                            <button
                              onClick={() => handleDuplicate(row)}
                              className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 px-3 py-1.5 rounded-lg transition-colors text-xs font-semibold border border-transparent hover:border-indigo-100 flex items-center gap-1"
                              title="Duplicate / Copy this demand to raise a new one"
                            >
                              <Copy size={14} /> Duplicate
                            </button>
                            <button
                              onClick={() => { setEditingItem(row); setModalOpen(true); }}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-2.5 py-1.5 rounded-lg transition-colors text-xs font-semibold border border-transparent hover:border-blue-100 flex items-center gap-1"
                              title="Edit Raw Master Record"
                            >
                              <Edit size={14} /> Edit
                            </button>
                            <button
                              onClick={() => handleDelete(row.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 px-2.5 py-1.5 rounded-lg transition-colors text-xs font-semibold border border-transparent hover:border-red-100 flex items-center gap-1"
                              title="Delete Record"
                            >
                              <Trash2 size={14} />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>



        {isModalOpen && (
          <div className="fixed inset-0 bg-gray-900/70 backdrop-blur-md z-50 flex items-center justify-center p-4 md:p-8">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl md:max-w-6xl overflow-hidden transform transition-all scale-100 flex flex-col max-h-[92vh] border border-gray-100">
              <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-indigo-950 px-6 py-4 flex justify-between items-center shrink-0 border-b border-slate-700">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  {editingItem ? <><FileSpreadsheet size={20} className="text-amber-400" /> {user.role === 'Admin' ? 'Edit Master Record' : 'Update Supply Details'}</> : <><Plus size={20} className="text-blue-400" /> Raise New Demand</>}
                </h3>
                <button onClick={() => setModalOpen(false)} className="text-slate-400 hover:text-white text-2xl font-light leading-none transition-colors">&times;</button>
              </div>
              <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto flex-1">
                {!editingItem && (
                  <div className="space-y-6">
                    {/* Master Details Panel */}
                    <div className="bg-slate-50/80 p-5 rounded-xl border border-slate-200 shadow-sm">
                      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">1. Demand Slip Details</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Demand Date (Raised On)</label>
                          <input
                            name="raised_on"
                            type="date"
                            value={demandHeader.raised_on}
                            onChange={e => setDemandHeader(prev => ({ ...prev, raised_on: e.target.value }))}
                            required
                            className="w-full border border-gray-300 p-2 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none font-medium text-gray-800 cursor-pointer shadow-sm text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Due Date (Required On)</label>
                          <input
                            name="required_date"
                            type="date"
                            value={demandHeader.required_date}
                            onChange={e => setDemandHeader(prev => ({ ...prev, required_date: e.target.value }))}
                            required
                            className="w-full border border-gray-300 p-2 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none font-medium text-gray-800 cursor-pointer shadow-sm text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Project</label>
                          <select
                            name="project"
                            value={demandHeader.project}
                            onChange={e => setDemandHeader(prev => ({ ...prev, project: e.target.value }))}
                            required
                            className="w-full border border-gray-300 p-2 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none font-medium text-gray-800 shadow-sm text-sm"
                          >
                            <option value="">Select Project</option>
                            {PROJECT_LIST.map(p => <option key={p} value={p}>{p}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Priority</label>
                          <select
                            name="priority"
                            value={demandHeader.priority}
                            onChange={e => setDemandHeader(prev => ({ ...prev, priority: e.target.value }))}
                            className="w-full border border-gray-300 p-2 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none font-medium text-gray-800 shadow-sm text-sm"
                          >
                            <option>Medium</option>
                            <option>High</option>
                            <option>Low</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Slip No / Demand ID</label>
                          <input
                            name="id"
                            value={demandHeader.id}
                            onChange={e => setDemandHeader(prev => ({ ...prev, id: e.target.value }))}
                            placeholder="Auto or Manual ID"
                            className="w-full border border-gray-300 p-2 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none font-medium shadow-sm text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Job #</label>
                          <input
                            name="job_number"
                            value={demandHeader.job_number}
                            onChange={e => setDemandHeader(prev => ({ ...prev, job_number: e.target.value }))}
                            placeholder="Job No."
                            className="w-full border border-gray-300 p-2 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none font-medium shadow-sm text-sm"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Overall Remarks / Location</label>
                          <input
                            name="construction_remarks"
                            value={demandHeader.construction_remarks}
                            onChange={e => setDemandHeader(prev => ({ ...prev, construction_remarks: e.target.value }))}
                            placeholder="e.g. House # 5801, 5796 / Ground Floor"
                            className="w-full border border-gray-300 p-2 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none font-medium shadow-sm text-sm"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Items List Panel */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">2. Demanded Items List</span>
                          <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2.5 py-0.5 rounded-full">{demandItems.length} {demandItems.length === 1 ? 'item' : 'items'}</span>
                        </div>
                        <button type="button" onClick={handleAddItem} className="text-blue-600 hover:text-blue-700 text-xs flex items-center gap-1.5 font-bold bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition-all border border-blue-200">
                          <Plus size={15} /> Add Item Row
                        </button>
                      </div>

                      <div className="space-y-2.5 max-h-[46vh] overflow-y-auto pr-2">
                        {demandItems.map((row, idx) => (
                          <div key={idx} className="flex flex-col md:flex-row gap-2.5 items-center bg-white p-3 rounded-xl border border-gray-200 shadow-sm hover:border-blue-300 transition-all">
                            <span className="text-xs font-mono font-bold text-slate-500 bg-slate-100 px-2 py-1.5 rounded-lg shrink-0">#{idx + 1}</span>
                            <div className="flex-1 w-full md:w-2/5">
                              <input className="w-full border border-gray-300 p-2 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Item Name (e.g. Door Beading)" value={row.item} list={`li-${idx}`} onChange={e => { const n = [...demandItems]; n[idx].item = e.target.value; setDemandItems(n); }} />
                              <datalist id={`li-${idx}`}>{CONSTRUCTION_ITEMS.map(ci => <option key={ci} value={ci} />)}</datalist>
                            </div>
                            <div className="flex gap-2 w-full md:w-auto">
                              <input className="w-24 border border-gray-300 p-2 rounded-lg text-sm font-bold focus:ring-2 focus:ring-blue-500 outline-none text-center" placeholder="Qty" type="number" value={row.demanded_qty} onChange={e => { const n = [...demandItems]; n[idx].demanded_qty = e.target.value; setDemandItems(n); }} />
                              <input className="w-28 border border-gray-300 p-2 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none text-center" placeholder="Unit (Rft, Ltr)" list="un" value={row.unit} onChange={e => { const n = [...demandItems]; n[idx].unit = e.target.value; setDemandItems(n); }} />
                            </div>
                            <div className="flex-1 w-full">
                              <input className="w-full border border-gray-300 p-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Description / Specs (Optional)" value={row.description} onChange={e => { const n = [...demandItems]; n[idx].description = e.target.value; setDemandItems(n); }} />
                            </div>
                            <button type="button" onClick={() => handleRemoveItem(idx)} className="text-gray-400 hover:text-red-600 hover:bg-red-50 p-2 rounded-lg transition-colors shrink-0" title="Remove Item">
                              <X size={18} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                {editingItem && (
                  <div className="space-y-4">
                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider border-b pb-1">Master Information</h4>

                    {user.role === 'Admin' ? (
                      <div className="space-y-4 bg-blue-50/30 p-4 rounded-xl border border-blue-100 mb-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-[10px] font-black text-blue-600 uppercase mb-1">Edit Item Name</label>
                            <input name="item" defaultValue={editingItem.item} className="w-full border p-2 rounded bg-white text-sm font-bold focus:ring-2 focus:ring-blue-500 outline-none" />
                          </div>
                          <div>
                            <label className="block text-[10px] font-black text-blue-600 uppercase mb-1">Edit Job #</label>
                            <input name="job_number" defaultValue={editingItem.job_number} className="w-full border p-2 rounded bg-white text-sm font-bold focus:ring-2 focus:ring-blue-500 outline-none" />
                          </div>
                          <div>
                            <label className="block text-[10px] font-black text-blue-600 uppercase mb-1">Edit Demand Date</label>
                            <input name="raised_on" type="date" defaultValue={editingItem.raised_on || ''} className="w-full border p-2 rounded bg-white text-sm font-bold focus:ring-2 focus:ring-blue-500 outline-none" />
                          </div>
                          <div>
                            <label className="block text-[10px] font-black text-blue-600 uppercase mb-1">Edit Due Date</label>
                            <input name="required_date" type="date" defaultValue={editingItem.required_date || ''} className="w-full border p-2 rounded bg-white text-sm font-bold focus:ring-2 focus:ring-blue-500 outline-none" />
                          </div>
                          <div>
                            <label className="block text-[10px] font-black text-blue-600 uppercase mb-1">Edit Project</label>
                            <select name="project" defaultValue={editingItem.project} className="w-full border p-2 rounded bg-white text-sm font-bold focus:ring-2 focus:ring-blue-500 outline-none">
                              {PROJECT_LIST.map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-[10px] font-black text-blue-600 uppercase mb-1">Edit Demanded Qty</label>
                            <input name="demanded_qty" type="number" defaultValue={editingItem.demanded_qty} className="w-full border p-2 rounded bg-white text-sm font-bold focus:ring-2 focus:ring-blue-500 outline-none" />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Item & Job</label>
                          <div className="p-3 bg-gray-50 rounded-lg text-sm font-bold text-gray-800">{editingItem.item} ({editingItem.job_number || 'N/A'})</div>
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Demand ID</label>
                          <div className="p-3 bg-gray-50 rounded-lg text-sm font-mono text-gray-600">{editingItem.id}</div>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-100">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Supplied Qty</label>
                        <input name="supplied_qty" type="number" required defaultValue={editingItem.supplied_qty} className="w-full border p-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Date Supplied</label>
                        <input name="supplied_date" type="date" defaultValue={editingItem.supplied_date || new Date().toISOString().split('T')[0]} className="w-full border p-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Procurement Remarks</label>
                      <textarea name="procurement_remarks" defaultValue={editingItem.procurement_remarks} className="w-full border p-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" rows="2"></textarea>
                    </div>
                  </div>
                )}
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 mt-6 shrink-0 md:pb-0 pb-20">
                  <button type="button" onClick={() => setModalOpen(false)} className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium">Cancel</button>
                  <button type="submit" disabled={loading} className="px-5 py-2.5 bg-blue-600 text-white rounded-lg font-medium shadow-md flex items-center gap-2">
                    {loading ? 'Saving...' : <>{editingItem ? 'Update Record' : 'Submit Items'} <MessageCircle size={16} /></>}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* --- SLIP-WIDE SUPPLY BATCH UPDATE MODAL --- */}
        {isSlipModalOpen && selectedSlip && (
          <div className="fixed inset-0 bg-gray-900/75 backdrop-blur-md z-50 flex items-center justify-center p-3 md:p-6 animate-in fade-in duration-200">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-6xl overflow-hidden flex flex-col max-h-[94vh] border border-gray-100">
              
              {/* Slip Header Banner */}
              <div className="bg-gradient-to-r from-[#0f172a] via-[#1e293b] to-[#047857] px-6 py-4 flex flex-col sm:flex-row justify-between sm:items-center gap-3 shrink-0 border-b border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center text-emerald-300">
                    <Truck size={22} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-black text-white tracking-tight">Supply Dispatch & Update</h3>
                      <span className="bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 text-xs font-mono font-bold px-2.5 py-0.5 rounded-full">
                        Slip #{selectedSlip.slipId}
                      </span>
                    </div>
                    <p className="text-xs text-slate-300 font-medium">
                      Project: <span className="text-white font-bold">{selectedSlip.project}</span> • Job #: <span className="text-white font-bold">{selectedSlip.job_number || 'N/A'}</span> • Due: <span className="text-amber-300 font-bold">{selectedSlip.required_date}</span>
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 self-end sm:self-center">
                  <span className="text-xs font-bold text-slate-300 bg-white/10 px-3 py-1.5 rounded-xl border border-white/10">
                    {selectedSlip.items.length} Demanded {selectedSlip.items.length === 1 ? 'Item' : 'Items'}
                  </span>
                  <button
                    onClick={() => { setIsSlipModalOpen(false); setSelectedSlip(null); }}
                    className="text-slate-400 hover:text-white text-2xl font-light leading-none p-1 transition-colors"
                  >
                    &times;
                  </button>
                </div>
              </div>

              {/* Master Supply Controls Bar */}
              <form onSubmit={handleSaveSlipSupplies} className="flex flex-col flex-1 overflow-hidden">
                <div className="bg-slate-50 p-4 border-b border-slate-200 shrink-0 grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
                  <div className="md:col-span-3">
                    <label className="block text-[11px] font-bold text-gray-600 uppercase tracking-wider mb-1">Supplied / Dispatch Date</label>
                    <input
                      type="date"
                      value={slipBatchDate}
                      onChange={e => setSlipBatchDate(e.target.value)}
                      required
                      className="w-full border border-gray-300 p-2 rounded-xl bg-white text-xs font-bold text-gray-800 focus:ring-2 focus:ring-emerald-500 outline-none shadow-sm cursor-pointer"
                    />
                  </div>

                  <div className="md:col-span-5">
                    <label className="block text-[11px] font-bold text-gray-600 uppercase tracking-wider mb-1">Procurement / Dispatch Remarks</label>
                    <input
                      type="text"
                      placeholder="e.g. Delivered via Truck # 8812 / Sent by Supplier"
                      value={slipBatchRemarks}
                      onChange={e => setSlipBatchRemarks(e.target.value)}
                      className="w-full border border-gray-300 p-2 rounded-xl bg-white text-xs font-medium text-gray-800 focus:ring-2 focus:ring-emerald-500 outline-none shadow-sm"
                    />
                  </div>

                  <div className="md:col-span-4 flex items-end gap-2 justify-end">
                    <button
                      type="button"
                      onClick={handleMarkAllFullSupply}
                      className="flex-1 py-2 px-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all shadow-md shadow-emerald-500/20 flex items-center justify-center gap-1.5 active:scale-95"
                      title="Set all item supplied quantities equal to demanded quantities"
                    >
                      <CheckCheck size={16} /> Mark All Full Supply
                    </button>
                    <button
                      type="button"
                      onClick={handleClearAllSupply}
                      className="py-2 px-3 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl text-xs font-bold transition-all active:scale-95"
                      title="Reset all supplied quantities to zero"
                    >
                      <RefreshCw size={14} /> Clear
                    </button>
                  </div>
                </div>

                {/* Construction Remarks / Location Banner */}
                {selectedSlip.construction_remarks && (
                  <div className="bg-amber-50/80 px-6 py-2 border-b border-amber-100 flex items-center gap-2 text-xs text-amber-900 font-medium">
                    <span className="font-bold uppercase tracking-wider text-[10px] bg-amber-200/60 text-amber-800 px-2 py-0.5 rounded">Site Location / Remarks:</span>
                    <span>{selectedSlip.construction_remarks}</span>
                  </div>
                )}

                {/* Items Grid / Table Workspace */}
                <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-3">
                  <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2 text-[11px] font-bold text-gray-400 uppercase tracking-wider bg-gray-50 rounded-xl">
                    <div className="col-span-1">#</div>
                    <div className="col-span-4">Item & Specifications</div>
                    <div className="col-span-2 text-center">Demanded</div>
                    <div className="col-span-3 text-center">Supplied Qty</div>
                    <div className="col-span-2 text-center">Balance & Status</div>
                  </div>

                  {selectedSlip.items.map((row, idx) => {
                    const balance = Math.max(0, row.demanded_qty - (Number(row.supplied_qty) || 0));
                    const isComplete = Number(row.supplied_qty) >= Number(row.demanded_qty) && Number(row.demanded_qty) > 0;
                    const isPartial = Number(row.supplied_qty) > 0 && !isComplete;

                    return (
                      <div
                        key={row.id || idx}
                        className={`p-4 rounded-2xl border transition-all flex flex-col md:grid md:grid-cols-12 gap-3 items-center ${
                          isComplete ? 'bg-emerald-50/40 border-emerald-200' : isPartial ? 'bg-amber-50/30 border-amber-200' : 'bg-white border-gray-200 shadow-sm'
                        }`}
                      >
                        {/* Index */}
                        <div className="col-span-1 flex items-center gap-2 self-start md:self-center">
                          <span className="w-7 h-7 rounded-lg bg-slate-100 text-slate-700 font-mono font-bold text-xs flex items-center justify-center">
                            #{idx + 1}
                          </span>
                        </div>

                        {/* Item Name & Description */}
                        <div className="col-span-4 w-full">
                          <h4 className="font-bold text-gray-900 text-sm leading-snug">{row.item}</h4>
                          {row.description && (
                            <p className="text-xs text-gray-500 font-medium mt-0.5">{row.description}</p>
                          )}
                          <span className="text-[10px] text-slate-400 font-mono">ID: {row.id}</span>
                        </div>

                        {/* Demanded Qty */}
                        <div className="col-span-2 text-center w-full flex md:flex-col justify-between md:justify-center items-center">
                          <span className="md:hidden text-xs text-gray-400 font-bold uppercase">Demanded:</span>
                          <span className="font-black text-gray-800 text-sm bg-slate-100 px-3 py-1 rounded-xl">
                            {row.demanded_qty} <span className="text-xs font-bold text-gray-500">{row.unit}</span>
                          </span>
                        </div>

                        {/* Supplied Qty Input & Quick Fill */}
                        <div className="col-span-3 w-full flex items-center justify-center gap-2">
                          <span className="md:hidden text-xs text-gray-400 font-bold uppercase">Supplied:</span>
                          <div className="relative flex-1 max-w-[140px]">
                            <input
                              type="number"
                              min="0"
                              max={row.demanded_qty * 2}
                              value={row.supplied_qty}
                              onChange={e => handleItemSupplyChange(idx, 'supplied_qty', e.target.value)}
                              className={`w-full border text-center p-2 rounded-xl text-sm font-black outline-none focus:ring-2 transition-all ${
                                isComplete
                                  ? 'border-emerald-400 bg-white text-emerald-700 focus:ring-emerald-500 shadow-sm'
                                  : isPartial
                                  ? 'border-amber-400 bg-white text-amber-700 focus:ring-amber-500 shadow-sm'
                                  : 'border-gray-300 bg-white text-gray-800 focus:ring-blue-500 shadow-sm'
                              }`}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => handleItemSupplyChange(idx, 'supplied_qty', row.demanded_qty)}
                            className="text-[11px] font-bold bg-slate-100 hover:bg-emerald-100 text-slate-700 hover:text-emerald-800 px-2.5 py-2 rounded-xl transition-all border border-slate-200 shrink-0"
                            title="Fill Full Quantity"
                          >
                            Full
                          </button>
                        </div>

                        {/* Balance & Status Badge */}
                        <div className="col-span-2 w-full flex md:flex-col justify-between md:justify-center items-end md:items-center gap-1">
                          <span className={`text-[11px] font-black px-2.5 py-0.5 rounded-full uppercase tracking-wider ${
                            isComplete
                              ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                              : isPartial
                              ? 'bg-amber-100 text-amber-800 border border-amber-200'
                              : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                          }`}>
                            {isComplete ? 'Complete' : isPartial ? 'Partial' : 'Pending'}
                          </span>
                          <span className="text-[10px] text-gray-500 font-bold">
                            Balance: <span className={balance === 0 ? 'text-emerald-600 font-black' : 'text-rose-600 font-black'}>{balance} {row.unit}</span>
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Modal Footer with Summary & Submission */}
                <div className="bg-slate-50 p-4 md:p-6 border-t border-slate-200 shrink-0 flex flex-col sm:flex-row justify-between items-center gap-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Slip Summary:</span>
                    <span className="bg-white border border-gray-200 text-gray-700 font-bold text-xs px-2.5 py-1 rounded-lg">
                      {selectedSlip.items.length} Items
                    </span>
                    <span className="bg-emerald-100 border border-emerald-200 text-emerald-800 font-bold text-xs px-2.5 py-1 rounded-lg">
                      {selectedSlip.items.filter(i => Number(i.supplied_qty) >= Number(i.demanded_qty) && Number(i.demanded_qty) > 0).length} Complete
                    </span>
                    <span className="bg-amber-100 border border-amber-200 text-amber-800 font-bold text-xs px-2.5 py-1 rounded-lg">
                      {selectedSlip.items.filter(i => Number(i.supplied_qty) > 0 && Number(i.supplied_qty) < Number(i.demanded_qty)).length} Partial
                    </span>
                    <span className="bg-yellow-100 border border-yellow-200 text-yellow-800 font-bold text-xs px-2.5 py-1 rounded-lg">
                      {selectedSlip.items.filter(i => Number(i.supplied_qty) === 0).length} Pending
                    </span>
                  </div>

                  <div className="flex items-center gap-3 w-full sm:w-auto">
                    <button
                      type="button"
                      onClick={() => { setIsSlipModalOpen(false); setSelectedSlip(null); }}
                      className="flex-1 sm:flex-none px-5 py-2.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-xl font-bold text-xs transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 sm:flex-none px-6 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-500 hover:to-teal-600 text-white rounded-xl font-bold text-xs shadow-lg shadow-emerald-500/25 flex items-center justify-center gap-2 active:scale-95 transition-all"
                    >
                      {loading ? (
                        <span>Saving Supplies...</span>
                      ) : (
                        <>
                          <Save size={16} /> Save All Slip Supplies
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>

            </div>
          </div>
        )}

        {showUserModal && (
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4 text-xs">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[80vh] flex flex-col overflow-hidden">
              <div className="bg-blue-900 text-white p-4 flex justify-between items-center select-none">
                <h3 className="font-bold text-lg flex items-center gap-2 relative">
                  <Shield size={20} /> User Management
                </h3>
                <button onClick={() => setShowUserModal(false)} className="hover:rotate-90 transition-transform">
                  <X className="text-white opacity-70 hover:opacity-100" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-6 bg-white">
                <ManageUsers />
              </div>
            </div>
          </div>
        )}

        {/* --- MOBILE BOTTOM NAVIGATION BAR --- */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 h-16 bg-[#0f172a]/95 backdrop-blur-lg border-t border-blue-900/50 shadow-2xl z-[100] flex items-center justify-around px-4">
          <button onClick={() => setActiveTab('home')} className={`p-3 transition-all ${activeTab === 'home' ? 'text-white' : 'text-blue-300/60'}`}>
            <TrendingUp size={activeTab === 'home' ? 26 : 22} />
          </button>
          <button onClick={() => setActiveTab('activity')} className={`p-3 transition-all ${activeTab === 'activity' ? 'text-white' : 'text-blue-300/60'}`}>
            <TrendingUp size={activeTab === 'activity' ? 26 : 22} className="rotate-90" />
          </button>
          <button onClick={() => setActiveTab('register')} className={`p-3 transition-all ${activeTab === 'register' ? 'text-white' : 'text-blue-300/60'}`}>
            <FileText size={activeTab === 'register' ? 26 : 22} />
          </button>
          <button onClick={() => setActiveTab('settings')} className={`p-3 transition-all ${activeTab === 'settings' ? 'text-white' : 'text-blue-300/60'}`}>
            <Shield size={activeTab === 'settings' ? 26 : 22} />
          </button>
        </div>
      </main>
    </div>
  );
}

// Inner Component for User Management
function ManageUsers() {
  const [users, setUsers] = useState([]);
  const [newForm, setNewForm] = useState({ name: '', role: 'Construction', password: '' });
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', role: 'Construction', password: '' });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { loadUsers(); }, []);
  const loadUsers = async () => {
    try {
      const res = await axios.get('/api/users');
      setUsers(res.data || []);
    } catch (e) {
      console.error("Error loading users", e);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newForm.name || !newForm.password) return;
    setLoading(true);
    try {
      await axios.post('/api/users', newForm);
      setNewForm({ name: '', role: 'Construction', password: '' });
      await loadUsers();
      alert("✅ User Created Successfully");
    } catch (e) {
      alert("Error creating user: " + (e.response?.data?.message || e.message));
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (u) => {
    setEditingUser(u);
    setEditForm({ name: u.name, role: u.role, password: u.password });
    setIsEditModalOpen(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    setLoading(true);
    try {
      await axios.put(`/api/users/${editingUser.id}`, editForm);
      setIsEditModalOpen(false);
      setEditingUser(null);
      await loadUsers();
      alert("✅ User Updated Successfully");
    } catch (e) {
      alert("Error updating user: " + (e.response?.data?.message || e.message));
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (id) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await axios.delete(`/api/users/${id}`);
        loadUsers();
      } catch (e) {
        alert("Error deleting user: " + e.message);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Create New User Section */}
      <form onSubmit={handleCreateUser} className="bg-slate-50 p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-2">
            <Plus size={14} className="text-blue-600" /> Create New User
          </h4>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
          <div>
            <label className="block text-xs font-bold uppercase text-gray-600 mb-1">Username / Name</label>
            <input
              className="w-full border border-gray-300 p-2.5 rounded-xl bg-white text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none"
              required
              value={newForm.name}
              onChange={e => setNewForm({ ...newForm, name: e.target.value })}
              placeholder="e.g. ahsan"
            />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase text-gray-600 mb-1">Assigned Role</label>
            <select
              className="w-full border border-gray-300 p-2.5 rounded-xl bg-white text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none"
              value={newForm.role}
              onChange={e => setNewForm({ ...newForm, role: e.target.value })}
            >
              <option value="Construction">Construction</option>
              <option value="Procurement">Procurement</option>
              <option value="Admin">Admin</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase text-gray-600 mb-1">Password</label>
            <input
              className="w-full border border-gray-300 p-2.5 rounded-xl bg-white text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none font-mono"
              required
              type="text"
              value={newForm.password}
              onChange={e => setNewForm({ ...newForm, password: e.target.value })}
              placeholder="Password"
            />
          </div>
          <div>
            <button
              type="submit"
              disabled={loading}
              className="w-full p-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-xs shadow-md shadow-blue-500/20 transition-all flex items-center justify-center gap-1.5"
            >
              <Plus size={15} /> Add User
            </button>
          </div>
        </div>
      </form>

      {/* Users Register Table */}
      <div className="border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
        <div className="bg-slate-100 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
          <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">Registered System Users ({users.length})</span>
        </div>
        <table className="w-full text-sm text-left">
          <thead className="bg-slate-50 uppercase text-gray-500 text-[11px] font-bold border-b border-gray-200">
            <tr>
              <th className="p-3.5">User Identity</th>
              <th className="p-3.5">Role</th>
              <th className="p-3.5">Password</th>
              <th className="p-3.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {users.map(u => (
              <tr key={u.id} className="hover:bg-slate-50/70 transition-colors">
                <td className="p-3.5 font-bold text-gray-900">{u.name}</td>
                <td className="p-3.5">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                    u.role === 'Admin' ? 'bg-purple-100 text-purple-800 border border-purple-200' :
                    u.role === 'Procurement' ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' :
                    'bg-blue-100 text-blue-800 border border-blue-200'
                  }`}>
                    {u.role}
                  </span>
                </td>
                <td className="p-3.5 font-mono text-gray-600">{u.password}</td>
                <td className="p-3.5 text-right space-x-2">
                  <button
                    type="button"
                    onClick={() => openEditDialog(u)}
                    className="px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 rounded-lg transition-all font-bold text-xs inline-flex items-center gap-1.5 shadow-sm"
                    title="Edit User"
                  >
                    <Edit size={13} /> Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteUser(u.id)}
                    className="px-2.5 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-lg transition-all font-bold text-xs inline-flex items-center gap-1 shadow-sm"
                    title="Delete User"
                  >
                    <Trash2 size={13} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dedicated Edit User Popup Dialog */}
      {isEditModalOpen && editingUser && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-[250] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-200 animate-in fade-in zoom-in-95 duration-150">
            <div className="bg-gradient-to-r from-amber-600 to-amber-700 text-white px-5 py-4 flex justify-between items-center">
              <h3 className="font-bold text-base flex items-center gap-2">
                <Edit size={18} /> Edit User: {editingUser.name}
              </h3>
              <button
                onClick={() => { setIsEditModalOpen(false); setEditingUser(null); }}
                className="text-white/80 hover:text-white text-xl font-bold leading-none p-1"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleUpdateUser} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase text-gray-700 mb-1">Username / Name</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 p-2.5 rounded-xl bg-white text-sm font-bold text-gray-900 focus:ring-2 focus:ring-amber-500 outline-none"
                  required
                  value={editForm.name}
                  onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-gray-700 mb-1">Assigned Role</label>
                <select
                  className="w-full border border-gray-300 p-2.5 rounded-xl bg-white text-sm font-bold text-gray-900 focus:ring-2 focus:ring-amber-500 outline-none"
                  value={editForm.role}
                  onChange={e => setEditForm({ ...editForm, role: e.target.value })}
                >
                  <option value="Construction">Construction</option>
                  <option value="Procurement">Procurement</option>
                  <option value="Admin">Admin</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-gray-700 mb-1">Password</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 p-2.5 rounded-xl bg-white text-sm font-mono font-bold text-gray-900 focus:ring-2 focus:ring-amber-500 outline-none"
                  required
                  value={editForm.password}
                  onChange={e => setEditForm({ ...editForm, password: e.target.value })}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => { setIsEditModalOpen(false); setEditingUser(null); }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-xl font-bold text-xs hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-5 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-bold text-xs shadow-md shadow-amber-500/20 flex items-center gap-1.5"
                >
                  <Save size={14} /> Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
